// ImportCard.js
// React 19.2.3 + antd 6.1.3
//
// Требуется зависимость:
//   npm i jszip
//
// Двухэтапный импорт:
// 1) Выбор файла (через antd Upload) + анализ ZIP (сигнатура, export.json, файлы, card/view)
// 2) Выбор "Импорт карточки" / "Импорт визуальных представлений" -> "Импорт" с прогрессбаром
// После успеха: кнопка "Закрыть", "Отмена" скрыта.
//
// Реальный импорт выполнен через Promise-функции:
// - importCardPromise()  -> вызывает importCardApi(...) и обрабатывает success/error
//

import React, { useMemo, useState } from "react";
import { connect } from 'react-redux'
import i18n from "i18next";
import { Modal, Tooltip, Checkbox, Button, Progress, Typography, Alert, Space, Divider, Upload, Input } from "antd";
import JSZip from "jszip";
import {ntriplesToInsertData, buildDeleteSPARQL, normalizeRdfFormat} from "./helper"
import settings from '../../virtuoso/settings';
import { GraphExchangeRepository } from '../../redux';

const { Text } = Typography;

function isZipBySignature(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);
  if (bytes.length < 4) return false;
  const b0 = bytes[0],
    b1 = bytes[1],
    b2 = bytes[2],
    b3 = bytes[3];

  const isPK = b0 === 0x50 && b1 === 0x4b;
  const isLocalHeader = b2 === 0x03 && b3 === 0x04;
  const isEOCD = b2 === 0x05 && b3 === 0x06;
  const isSpanned = b2 === 0x07 && b3 === 0x08;

  return isPK && (isLocalHeader || isEOCD || isSpanned);
}

function normalizeZipPath(p) {
  return String(p || "")
    .replace(/\\/g, "/")
    .replace(/^\.\/+/, "")
    .replace(/^\/+/, "")
    .trim();
}

function classifyManifestItem(item) {
  if (item && typeof item === "object") {
    if (item.card) return "card";    
  }
  return "other";
}

function buildErrorMessage(err, fallback = i18n.t("io.import.conceptscheme.errors.import")) {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  if (err?.message) return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return fallback;
  }
}

function getPurlServerFromIri(iri) {
  if (typeof iri !== "string" || !iri.trim()) return "";
  try {
    return new URL(iri).origin;
  } catch {
    return iri.trim();
  }
}

function replaceAllText(value, source, target) {
  if (typeof value !== "string" || !source) return value;
  return value.split(source).join(target);
}

function replacePurlInManifestItem(item, source, target) {
  if (!item || !source) return item;
  const next = { ...item };
  if (Array.isArray(next.IRIS)) {
    next.IRIS = next.IRIS.map((iri) => replaceAllText(iri, source, target));
  }
  if (next.card) {
    next.card = {
      ...next.card,
      scheme: replaceAllText(next.card.scheme, source, target),
    };
  }
  return next;
}


// Пример "реального" импорта: один API-вызов на карточку
// Возвращает Promise, который resolve при успехе или reject при ошибке.
function getImportFormat(file) {
  const manifestFormat = file.manifestItem?.format;
  if (manifestFormat) {
    normalizeRdfFormat(manifestFormat);
    return manifestFormat;
  }

  const extension = String(file.name || "").split(".").pop().toLowerCase();
  if (extension === "ttl") return "Turtle";
  if (["n3", "nt"].includes(extension)) return "N-Triples";
  throw new Error(i18n.t("io.import.conceptscheme.errors.unknownRdfFormat", { filename: file.name }));
}
async function importCardApi({ manifest, files, graph, deleteBeforeImport, dispatch, replacePurl, sourcePurlServer, targetPurlServer}) {
  for (const file of files) {
    const iri = file.manifestItem?.card?.scheme;
    const content = replacePurl
      ? replaceAllText(file.content, sourcePurlServer, targetPurlServer)
      : file.content;

    const sparqlDelete = (deleteBeforeImport && file.manifestItem?.card?.IRIs)
      ? await buildDeleteSPARQL(file.manifestItem?.card?.IRIs)
      : "";  

    const format = getImportFormat(file);
    const sparqlInsert = await ntriplesToInsertData(content, { format });
    const sparql = sparqlDelete + "\n"+sparqlInsert;
    const result = await GraphExchangeRepository.importSubjects(graph, sparql);
    if (result !== "OK")
      throw new Error(i18n.t("io.import.conceptscheme.errors.sparql"));
  }
  return { ok: true };
}

// -------------------------

function ImportConceptScheme({ open, onClose, title = i18n.t("io.import.conceptscheme.title"), graph = settings.graph.iri, dispatch}) {
  // stage: choose -> analyzing -> ready -> importing -> done -> error
  const [stage, setStage] = useState("choose");

  const [selectedName, setSelectedName] = useState("");
  const [percent, setPercent] = useState(0);
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState(null);

  const [analysis, setAnalysis] = useState(null);
  // analysis = {
  //   fileIndex: Map<string, JSZipObject>
  //   manifest: any[]
  //   items: [{ kind, fileNorm, manifestItem }]
  //   hasCards: boolean
  //   cardsCount: number
  // }

  const [importCard, setImportCard] = useState(true);
  const [deleteBeforeImport, setDeleteBeforeImport] = useState(false);
  const [replacePurl, setReplacePurl] = useState(false);
  const [sourcePurlServer, setSourcePurlServer] = useState("");
  const [targetPurlServer, setTargetPurlServer] = useState("");

  const isAnalyzing = stage === "analyzing";
  const isReady = stage === "ready";
  const isImporting = stage === "importing";
  const isDone = stage === "done";
  const canClose = stage === "choose" || stage === "ready" || stage === "done" || stage === "error";

  function resetAll() {
    setStage("choose");
    setSelectedName("");
    setPercent(0);
    setStatusText("");
    setError(null);
    setAnalysis(null);
    setImportCard(true);
    setDeleteBeforeImport(false);
    setReplacePurl(false);
    setSourcePurlServer("");
    setTargetPurlServer("");
  }

  function closeAndReset() {
    if (!canClose) return;
    onClose();
    resetAll();
  }

  function canImportAnything() {
    if (!analysis) return false;
    const wantCard = importCard && analysis.hasCards;  
    if (!wantCard) return false;
    return true;
  }

  function canPURLChange (){
    if (!analysis) return false;
    if (replacePurl) return !!sourcePurlServer.trim() && !!targetPurlServer.trim();
    return true;
  } 

  async function analyzeZipFile(file) {
    setError(null);
    setStage("analyzing");
    setSelectedName(file?.name || "");
    setPercent(0);
    setStatusText(i18n.t("io.import.conceptscheme.status.preparing"));

    try {
      setStatusText(i18n.t("io.import.conceptscheme.status.checkingZip"));
      const arrayBuffer = await file.arrayBuffer();
      if (!isZipBySignature(arrayBuffer)) throw new Error(i18n.t("io.import.conceptscheme.errors.notZip"));
      setPercent(15);

      setStatusText(i18n.t("io.import.conceptscheme.status.readingArchive"));
      const zip = await JSZip.loadAsync(arrayBuffer);
      const zipFiles = Object.values(zip.files).filter((f) => !f.dir);
      if (zipFiles.length === 0) throw new Error(i18n.t("io.import.conceptscheme.errors.emptyArchive"));
      setPercent(30);

      const fileIndex = new Map();
      for (const f of zipFiles) fileIndex.set(normalizeZipPath(f.name), f);

      setStatusText(i18n.t("io.import.conceptscheme.status.searchingManifest"));
      const manifestEntry = fileIndex.get("export.json");
      if (!manifestEntry) throw new Error(i18n.t("io.import.conceptscheme.errors.manifestMissing"));
      setPercent(45);

      setStatusText(i18n.t("io.import.conceptscheme.status.readingManifest"));
      const manifestText = await manifestEntry.async("string");

      let manifest;
      try {
        manifest = JSON.parse(manifestText);
      } catch {
        throw new Error(i18n.t("io.import.conceptscheme.errors.manifestInvalidJson"));
      }
      if (!Array.isArray(manifest)) throw new Error(i18n.t("io.import.conceptscheme.errors.manifestNotArray"));
      setPercent(60);

      setStatusText(i18n.t("io.import.conceptscheme.status.checkingManifestFiles"));
      const items = [];
      const missing = [];

      for (const m of manifest) {
        const fileRaw = m?.file;
        if (typeof fileRaw !== "string" || !fileRaw.trim()) continue;

        const fileNorm = normalizeZipPath(fileRaw);
        const kind = classifyManifestItem(m);
        getImportFormat({ name: fileNorm, manifestItem: m });

        items.push({ kind, fileNorm, manifestItem: m });
        if (!fileIndex.has(fileNorm)) missing.push(fileNorm);
      }

      if (items.length === 0) throw new Error(i18n.t("io.import.conceptscheme.errors.noManifestItems"));

      if (missing.length > 0) {
        const head = missing.slice(0, 10).join(", ");
        const tail = missing.length > 10 ? ` … (+${missing.length - 10})` : "";
        throw new Error(i18n.t("io.import.conceptscheme.errors.filesMissing", { files: `${head}${tail}` }));
      }

      if (!graph)
        throw new Error(i18n.t("io.import.conceptscheme.errors.graphMissing"));

      const cardsCount = items.filter((x) => x.kind === "card" &&  x.manifestItem.card.hasOwnProperty("scheme") ).length;

      const hasCards = cardsCount > 0;
      const firstScheme = items.find((x) => x.kind === "card")?.manifestItem?.card?.scheme;

      setImportCard(hasCards);
      setSourcePurlServer(getPurlServerFromIri(firstScheme));
      setTargetPurlServer("");
      setDeleteBeforeImport(false);
      setReplacePurl(false);

      setAnalysis({ fileIndex, manifest, items, hasCards, cardsCount });

      setPercent(0);
      setStatusText(i18n.t("io.import.conceptscheme.status.analysisComplete"));
      setStage("ready");
    } catch (e) {
      setStage("error");
      setPercent(0);
      setStatusText("");
      setAnalysis(null);
      setError(buildErrorMessage(e, i18n.t("io.import.conceptscheme.errors.analysis")));
    }
  }

  // Собираем файлы (распаковка) в формате [{name, content, manifestItem}]
  async function extractFiles(items, fileIndex) {
    const out = [];
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      const entry = fileIndex.get(it.fileNorm);
      const content = await entry.async("string");
      const manifestItem = replacePurl
        ? replacePurlInManifestItem(it.manifestItem, sourcePurlServer.trim(), targetPurlServer.trim())
        : it.manifestItem;
      out.push({ name: it.fileNorm, content, manifestItem });
    }
    return out;
  }

  // Отдельный promise импорта карточки: распаковать card-файлы -> вызвать API -> обработать успех/ошибку
  async function importCardPromise({ fileIndex, manifest, items }) {
    const cardItems = items.filter((it) => it.kind === "card");
    if (cardItems.length === 0) return;

    setStatusText(i18n.t("io.import.conceptscheme.status.extractingData"));
    setPercent(10);
    const files = await extractFiles(cardItems, fileIndex);

    setStatusText(i18n.t("io.import.conceptscheme.status.importingObject"));
    setPercent(35);

    try {
      await importCardApi({
        manifest,
        files,
        graph,
        dispatch,
        deleteBeforeImport,
        replacePurl,
        sourcePurlServer: sourcePurlServer.trim(),
        targetPurlServer: targetPurlServer.trim(),
      }); // <-- реальный вызов
      setStatusText(i18n.t("io.import.conceptscheme.status.objectImported"));
      setPercent(50);
    } catch (e) {
      throw new Error(i18n.t("io.import.conceptscheme.errors.objectImport", { error: buildErrorMessage(e) }));
    }
  }

  async function runImport() {
    if (!analysis) return;

    setError(null);
    setStage("importing");
    setPercent(0);
    setStatusText(i18n.t("io.import.conceptscheme.status.preparingImport"));

    try {
      const doCard = importCard && analysis.hasCards;      
      if (!doCard) throw new Error(i18n.t("io.import.conceptscheme.errors.nothingSelected"));

      if (replacePurl && (!sourcePurlServer.trim() || !targetPurlServer.trim())) {
        throw new Error(i18n.t("io.import.conceptscheme.errors.purlRequired"));
      }

      // Вариант: последовательный импорт (сначала карточка, потом представления)
      // Можно поменять местами при необходимости.
      if (doCard) await importCardPromise(analysis);

      setPercent(100);
      setStatusText(i18n.t("io.import.conceptscheme.status.importComplete"));
      setStage("done");
    } catch (e) {
      setStage("error");
      setPercent(0);
      setStatusText("");
      setError(buildErrorMessage(e, i18n.t("io.import.conceptscheme.errors.import")));
    }
  }

  const footer = useMemo(() => {
    if (isDone) {
      return (
        <Button type="primary" onClick={closeAndReset}>
          {i18n.t("io.import.conceptscheme.close")}
        </Button>
      );
    }

    if (stage === "choose") {
      return (
        <Space>
          <Button onClick={closeAndReset} disabled={!canClose}>
            {i18n.t("io.import.conceptscheme.cancel")}
          </Button>

          <Upload
            showUploadList={false}
            multiple={false}
            beforeUpload={(file) => {
              setTimeout(() => analyzeZipFile(file), 0);
              return false;
            }}
          >
            <Button type="primary" loading={isAnalyzing}>
              {i18n.t("io.import.conceptscheme.selectFile")}
            </Button>
          </Upload>
        </Space>
      );
    }

    if (stage === "ready") {
      return (
        <Space>
          <Button onClick={closeAndReset} disabled={!canClose}>
            {i18n.t("io.import.conceptscheme.cancel")}
          </Button>
          <Button type="primary" onClick={runImport} disabled={!canImportAnything() || !canPURLChange()} loading={isImporting}>
            {i18n.t("io.import.conceptscheme.import")}
          </Button>
        </Space>
      );
    }

    return (
      <Button type="primary" loading={isAnalyzing || isImporting} disabled>
        {i18n.t("io.import.conceptscheme.import")}
      </Button>
    );
   }, [stage, isDone, isAnalyzing, isImporting, canClose, analysis, importCard, deleteBeforeImport, replacePurl, sourcePurlServer, targetPurlServer])
  ;

  const summary = useMemo(() => {
    if (!analysis) return null;
    return (
      <Text type="secondary">
        {i18n.t("io.import.conceptscheme.foundSchemes", { count: analysis.cardsCount })}
      </Text>
    );
  }, [analysis]);

  return (
    <Modal
      title={title}
      open={open}
      onCancel={closeAndReset}
      footer={footer}
      maskClosable={canClose}
      closable={canClose}
      destroyOnHidden
    >
      <Divider style={{ margin: "12px 0" }} />

      <div>
        {selectedName
          ? <Text type="secondary">{i18n.t("io.import.conceptscheme.file", { filename: selectedName })}</Text>
          : <Text type="secondary">{i18n.t("io.import.conceptscheme.noFileSelected")}</Text>}
      </div>

      {isReady && analysis && (
        <div style={{ marginTop: 12 }}>
          <Text strong>{i18n.t("io.import.conceptscheme.whatToImport")}</Text>

          <div style={{ marginTop: 8 }}>
            <Checkbox checked={importCard} onChange={(e) => setImportCard(e.target.checked)} disabled={!analysis.hasCards || true}>
              {i18n.t("io.import.conceptscheme.importSchemes")}
            </Checkbox>
          </div>

          <div style={{ marginTop: 8 }}>
            <Checkbox checked={deleteBeforeImport} onChange={(e) => setDeleteBeforeImport(e.target.checked)} disabled={!analysis.hasCards || !importCard}>
              {i18n.t("io.import.conceptscheme.deleteBeforeImport")}
            </Checkbox>
          </div>

          <div style={{ marginTop: 8 }}>
            <Checkbox checked={replacePurl} onChange={(e) => setReplacePurl(e.target.checked)} disabled={!analysis.hasCards || !importCard}>
              {i18n.t("io.import.conceptscheme.replacePurlServer")}
            </Checkbox>
          </div>

          {replacePurl && (
            <Space direction="vertical" style={{ width: "100%", marginTop: 8 }}>
              <Input
                value={sourcePurlServer}
                onChange={(e) => setSourcePurlServer(e.target.value)}
                placeholder={i18n.t("io.import.conceptscheme.sourcePurlServer")}
              />
              <Input
                value={targetPurlServer}
                onChange={(e) => setTargetPurlServer(e.target.value)}
                placeholder={i18n.t("io.import.conceptscheme.targetPurlServer")}
              />
            </Space>
          )}

          <div style={{ marginTop: 8 }}>{summary}</div>

          {!canImportAnything() && (
            <div style={{ marginTop: 10 }}>
              <Alert
                type="warning"
                showIcon
                title={i18n.t("io.import.conceptscheme.warnings.nothingToImport")}
                description={i18n.t("io.import.conceptscheme.warnings.uploadAnotherFile")}
              />
            </div>
          )}
          {!canPURLChange() && (
            <div style={{ marginTop: 10 }}>
              <Alert
                type="warning"
                showIcon
                title={i18n.t("io.import.conceptscheme.warnings.configurationError")}
                description={i18n.t("io.import.conceptscheme.warnings.configurePurl")}
              />
            </div>
          )}

        </div>
      )}

      {(isAnalyzing || isImporting || isDone) && (
        <div style={{ marginTop: 16 }}>
          <Progress percent={percent} />
          {!!statusText && (
            <div style={{ marginTop: 8 }}>
              <Text>{statusText}</Text>
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 16 }}>
          <Alert type="error" title={i18n.t("io.import.conceptscheme.error")} description={error} showIcon />
        </div>
      )}

      {isDone && (
        <div style={{ marginTop: 16 }}>
          <Alert type="success" title={i18n.t("io.import.conceptscheme.success")} showIcon />
        </div>
      )}
    </Modal>
  );
}

const mapStateToProps = (state, ownProps) => {
  const { vadms } = state;
  return {
    ...ownProps,    
    graph: settings.catalog.graph.iri
  };  
}; 

export default connect(mapStateToProps)(ImportConceptScheme);
